import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: "What is Vigorbuy Spreadsheet?",
    answer:
      "Vigorbuy Spreadsheet is an organized resource that helps users explore fashion product categories, compare information, and discover useful product references efficiently. It presents 14 fashion categories in a structured format, making it easy to browse and navigate different types of products. Unlike traditional search engines that scatter results across multiple pages, the Vigorbuy Spreadsheet consolidates everything into one clean, scannable resource that is regularly updated with fresh information.",
  },
  {
    question: "Is Vigorbuy Spreadsheet safe to use?",
    answer:
      "Yes, the Vigorbuy Spreadsheet is safe to use. It is an informational resource that does not collect personal data, process payments, or require account creation. All links to external sites are clearly marked, and we encourage users to verify information independently before making any purchasing decisions. Our editorial team regularly reviews content to maintain accuracy and transparency.",
  },
  {
    question: "How often is the Vigorbuy Spreadsheet updated?",
    answer:
      "The Vigorbuy Spreadsheet is updated regularly to reflect the latest category structures, trending products, and relevant resources. Updates are typically made on a weekly or bi-weekly basis, ensuring that users always have access to current and accurate information. Major structural updates are announced and documented so users can track changes over time.",
  },
  {
    question: "Who should use Vigorbuy Spreadsheet?",
    answer:
      "Vigorbuy Spreadsheet is designed for anyone interested in organized fashion product discovery — from casual shoppers looking for inspiration to serious buyers who want to compare options efficiently. Beginners will appreciate the intuitive category structure and easy navigation, while experienced shoppers will find value in the comprehensive comparison capabilities and regularly updated resource lists.",
  },
  {
    question: "Can beginners use Vigorbuy Spreadsheet?",
    answer:
      "Absolutely. The Vigorbuy Spreadsheet is designed with beginners in mind. The category structure is intuitive and clearly labeled, navigation is straightforward, and each section includes helpful descriptions. If you are new to organized product discovery, you can start by browsing a category that interests you and gradually explore more advanced features like comparison tables and detailed guides.",
  },
  {
    question: "Is Vigorbuy Spreadsheet free?",
    answer:
      "Yes, the Vigorbuy Spreadsheet resource and this informational website are completely free to access. There are no subscription fees, hidden charges, or premium tiers. Our mission is to provide organized, transparent product discovery resources to everyone at no cost. Simply visit the site, explore the categories, and use the guides to enhance your shopping research.",
  },
  {
    question: "How is Vigorbuy Spreadsheet different from Google search?",
    answer:
      "Unlike Google search, which returns millions of scattered results requiring extensive filtering, the Vigorbuy Spreadsheet presents fashion product categories in an organized, pre-structured format. This eliminates the need to open dozens of tabs, compare conflicting information, or sift through irrelevant results. The spreadsheet model provides a curated, side-by-side comparison experience that traditional search engines cannot replicate.",
  },
  {
    question: "Do I need to create an account to use Vigorbuy Spreadsheet?",
    answer:
      "No account creation is required. The Vigorbuy Spreadsheet and all associated informational resources on this website are accessible without registration, login, or any personal information submission. We believe in frictionless access — you can start exploring categories and reading guides immediately upon visiting the site.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // FAQPage Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <section id="faq" className="py-16 md:py-24 bg-background-50">
      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-xs font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full mb-4 tracking-wide">
              COMMON QUESTIONS
            </span>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              Frequently Asked{" "}
              <span className="text-accent-600">Questions</span>
            </h2>
            <p className="text-sm md:text-base text-foreground-500">
              Everything you need to know about the Vigorbuy Spreadsheet,
              answered clearly and honestly.
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-3">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden transition-all duration-200"
              >
                <button
                  onClick={() => toggleFAQ(idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-secondary-50/50 transition-colors duration-200"
                >
                  <span className="text-sm md:text-base font-semibold text-foreground-800 pr-4">
                    {item.question}
                  </span>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      openIndex === idx
                        ? "bg-accent-500 text-background-50 rotate-45"
                        : "bg-background-100 text-foreground-500"
                    }`}
                  >
                    <i className="ri-add-line text-sm"></i>
                  </div>
                </button>
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    openIndex === idx ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}